<template>
  <div class="brand-selector">
    <!-- 搜索框 -->
    <div class="search-container">
      <van-search v-model="searchKeyword" placeholder="请输入搜索关键词" @input="onSearch" @clear="onClear"
        background="#f6f7fc" />
    </div>

    <!-- 品牌列表 -->
    <div class="list-container" ref="listContainer">
      <div v-for="(group, letter) in filteredData" :key="letter" class="letter-group">
        <div class="letter-header" :id="`letter-${letter}`">{{ letter }}</div>
        <van-cell v-for="item in group" :key="item.id" :title="item.fullName" @click="selectItem(item)"
          class="brand-item" />
      </div>
      <div v-if="Object.keys(filteredData).length === 0" class="no-result">
        暂无搜索结果
      </div>
    </div>

    <!-- 右侧字母导航 -->
    <div class="letter-nav">
      <div v-for="letter in letters" :key="letter" class="letter-item" :class="{ active: currentLetter === letter }"
        @click="scrollToLetter(letter)">
        {{ letter }}
      </div>
    </div>
  </div>
</template>

<script>
import { DictionaryData } from "@/api/index";
import { pinyin } from 'pinyin-pro';

export default {
  name: "BrandSelector",
  data() {
    return {
      searchKeyword: "",
      dataList: [],
      filteredData: {},
      letters: [],
      currentLetter: "",
      type: "", // "brand" 或 "carType"
      typeId: "", // 接口需要的类型ID
    };
  },
  mounted() {
    this.initPage();
  },
  activated() {
    // 每次进入页面时都会执行
    console.log('页面重新激活');
    this.initPage();
  },
  methods: {
    initPage() {
      // 从路由参数获取类型
      this.type = this.$route.query.type || "brand";
      this.typeId = this.type === "brand"
        ? "a07372f561ff4b1aa535ba2e552b9c2d"
        : "b4c3635a05f24230a3420cb95cc21e48";

      this.fetchData();
    },
    fetchData() {
      DictionaryData(this.typeId).then((res) => {
        this.dataList = res.data.list || [];
        this.processData();
      });
    },
    processData() {
      // 按首字母分组
      const grouped = {};
      const letterSet = new Set();

      this.dataList.forEach(item => {
        // 获取首字母，如果没有则使用 #
        let firstLetter = this.getFirstLetter(item.fullName);
        if (!firstLetter) firstLetter = "#";

        if (!grouped[firstLetter]) {
          grouped[firstLetter] = [];
        }
        grouped[firstLetter].push(item);
        letterSet.add(firstLetter);
      });

      // 对每个分组内的数据按名称排序
      Object.keys(grouped).forEach(letter => {
        grouped[letter].sort((a, b) => a.fullName.localeCompare(b.fullName, 'zh-CN'));
      });

      // 获取排序后的字母列表
      this.letters = Array.from(letterSet).sort((a, b) => {
        // # 字符排在最后
        if (a === '#') return 1;
        if (b === '#') return -1;
        return a.localeCompare(b);
      });

      // 创建排序后的filteredData对象
      const sortedGrouped = {};
      this.letters.forEach(letter => {
        sortedGrouped[letter] = grouped[letter];
      });
      
      this.filteredData = sortedGrouped;
    },
    getFirstLetter(str) {
      if (!str) return "";

      try {
        // 使用 pinyin-pro 获取拼音首字母
        const firstPinyin = pinyin(str.charAt(0), { toneType: 'none', type: 'first' });
        // 确保只返回第一个字符（首字母）
        const firstLetter = firstPinyin ? firstPinyin.charAt(0).toUpperCase() : "#";
        return firstLetter;
      } catch (error) {
        console.error('获取拼音失败:', error);
        return "#";
      }
    },
    onSearch() {
      if (!this.searchKeyword.trim()) {
        this.processData();
        return;
      }

      const keyword = this.searchKeyword.toLowerCase();
      const filtered = {};
      const letterSet = new Set();

      this.dataList.forEach(item => {
        if (item.fullName.toLowerCase().includes(keyword)) {
          let firstLetter = this.getFirstLetter(item.fullName);
          if (!firstLetter) firstLetter = "#";

          if (!filtered[firstLetter]) {
            filtered[firstLetter] = [];
          }
          filtered[firstLetter].push(item);
          letterSet.add(firstLetter);
        }
      });

      // 对每个分组内的数据按名称排序
      Object.keys(filtered).forEach(letter => {
        filtered[letter].sort((a, b) => a.fullName.localeCompare(b.fullName, 'zh-CN'));
      });

      // 获取排序后的字母列表
      this.letters = Array.from(letterSet).sort((a, b) => {
        // # 字符排在最后
        if (a === '#') return 1;
        if (b === '#') return -1;
        return a.localeCompare(b);
      });

      // 创建排序后的filteredData对象
      const sortedFiltered = {};
      this.letters.forEach(letter => {
        sortedFiltered[letter] = filtered[letter];
      });
      
      this.filteredData = sortedFiltered;
    },
    onClear() {
      this.searchKeyword = "";
      this.processData();
    },
    scrollToLetter(letter) {
      const element = document.getElementById(`letter-${letter}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        this.currentLetter = letter;

        // 重置当前字母高亮
        setTimeout(() => {
          this.currentLetter = "";
        }, 1000);
      }
    },
    selectItem(item) {
      // 先存储选中的数据到sessionStorage
      sessionStorage.setItem("selectedItem", JSON.stringify({
        type: this.type,
        data: item
      }));

      // 然后返回上一页
      this.$router.go(-1);
    }
  }
};
</script>

<style scoped>
.brand-selector {
  position: relative;
  height: 100vh;
  background: #f6f7fc;
  display: flex;
  flex-direction: column;
}

.search-container {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #f6f7fc;
  padding: 10px 0;
}

.list-container {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 50px;
}

.letter-group {
  margin-bottom: 10px;
}

.letter-header {
  background: #ebedf0;
  padding: 8px 16px;
  font-weight: bold;
  color: #323233;
  font-size: 14px;
}

.brand-item {
  background: #fff;
}

.letter-nav {
  position: fixed;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  z-index: 10;
}

.letter-item {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #666;
  margin: 2px 0;
  cursor: pointer;
}

.letter-item.active {
  color: #01ABCF;
  font-weight: bold;
}

.no-result {
  text-align: center;
  padding: 50px 0;
  color: #969799;
}
</style>